<script lang="ts">
export default {
  inheritAttrs: false
};
</script>

<script setup lang="ts">
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeTooltip from "@dsk/components/LeTooltip.vue";
import LeBackspace from "@dsk/icons/LeBackspace.vue";
import LeCross from "@dsk/icons/LeCross.vue";
import LeKeyboard from "@dsk/icons/LeKeyboard.vue";
import { nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { vMatomoEvent } from "@/directives";
import { ArabicKeyboardHelper } from "@/helpers";
import { useFloatingKeyboardStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

defineProps<{
  buttonId?: string;
}>();

const emit = defineEmits<{
  (event: "key", key: ArabicKeyboardHelper.Key): void;
  (event: "close"): void;
}>();

const { t } = useI18n();

const floatingKeyboardStore = useFloatingKeyboardStore();

const EVENT_LABEL_OPEN = "open";
const EVENT_LABEL_CLOSE = "close";
const EVENT_LABEL_KEY_PRESS = "key-press";

const keyboardOverlay = ref<undefined | HTMLElement>(undefined);
const state = floatingKeyboardStore.getState("arabic");
const offsetX = ref<number>(0);
const offsetY = ref<number>(0);

const onToggle = () => {
  state.isOpen = !state.isOpen;

  if (state.isOpen === false) {
    emit("close");
  }
};

const onClose = () => {
  state.isOpen = false;
  emit("close");
};

const centerKeyboard = () => {
  if (!keyboardOverlay.value) {
    return;
  }

  state.posX = window.innerWidth / 2 - keyboardOverlay.value.offsetWidth / 2;
  state.posY = window.innerHeight / 2 - keyboardOverlay.value.offsetHeight / 2;
};

const prepareDrag = (e: MouseEvent) => {
  offsetY.value = e.clientY - state.posY;
  offsetX.value = e.clientX - state.posX;
  document.body.addEventListener("mousemove", drag);
};

const drag = (e: MouseEvent) => {
  if ((e.buttons & 1) === 0) {
    document.body.removeEventListener("mousemove", drag);
    return;
  }

  state.posX = e.clientX - offsetX.value;
  // 70 = height of the header
  state.posY = Math.max(70, e.clientY - offsetY.value);
};

watch(
  () => state.isOpen,
  (open) => {
    nextTick(() => {
      if (state.posX === 0 && state.posY === 0) {
        centerKeyboard();
      }
    });

    if (open) {
      window.addEventListener("resize", centerKeyboard);
    } else {
      window.removeEventListener("resize", centerKeyboard);
    }
  }
);
</script>

<template>
  <LeTooltip
    v-bind="$attrs"
    :text="
      t('le-arabic-keyboard.keyboard-tooltip.text', {
        action: state.isOpen
          ? t('le-arabic-keyboard.keyboard-tooltip.off')
          : t('le-arabic-keyboard.keyboard-tooltip.on')
      })
    "
    :margin-bottom="10"
  >
    <LeIconButton
      :id="buttonId"
      v-matomo-event:click="{
        category: MatomoEventCategory.ArabicKeyboard,
        action: MatomoEventAction.ArabicKeyboard,
        label: state.isOpen ? EVENT_LABEL_OPEN : EVENT_LABEL_CLOSE
      }"
      :aria-label="
        t('le-arabic-keyboard.keyboard-tooltip.text', {
          action: state.isOpen
            ? t('le-arabic-keyboard.keyboard-tooltip.off')
            : t('le-arabic-keyboard.keyboard-tooltip.on')
        })
      "
      :aria-pressed="state.isOpen"
      @click="onToggle()"
    >
      <LeKeyboard role="presentation" width="16" height="16" />
    </LeIconButton>
  </LeTooltip>
  <div
    v-show="state.isOpen"
    ref="keyboardOverlay"
    role="dialog"
    aria-labelledby="arabic-keyboard-title"
    class="arabic-keyboard"
    :style="{ left: state.posX + 'px', top: state.posY + 'px' }"
  >
    <div class="arabic-keyboard__header" @mousedown.prevent="prepareDrag($event)">
      <LeHeading id="arabic-keyboard-title" tag="h3" size="m">{{ t("le-arabic-keyboard.header-text") }}</LeHeading>
      <LeIconButton
        v-matomo-event:click="{
          category: MatomoEventCategory.ArabicKeyboard,
          action: MatomoEventAction.ArabicKeyboard,
          label: EVENT_LABEL_CLOSE
        }"
        type="button"
        @click="onClose()"
      >
        <LeCross role="presentation" />
      </LeIconButton>
    </div>
    <div class="arabic-keyboard__body">
      <div v-for="(row, rowIndex) in ArabicKeyboardHelper.keys" :key="rowIndex" class="arabic-keyboard__keyboard-row">
        <button
          v-for="key in row"
          :key="key"
          v-matomo-event:click="{
            category: MatomoEventCategory.ArabicKeyboard,
            action: MatomoEventAction.ArabicKeyboard,
            label: EVENT_LABEL_KEY_PRESS
          }"
          class="arabic-keyboard__button"
          type="button"
          @click="emit('key', key)"
        >
          <span>{{ key }}</span>
        </button>
        <button
          v-if="rowIndex === 0"
          v-matomo-event:click="{
            category: MatomoEventCategory.ArabicKeyboard,
            action: MatomoEventAction.ArabicKeyboard,
            label: EVENT_LABEL_KEY_PRESS
          }"
          class="arabic-keyboard__button arabic-keyboard__button--backspace"
          type="button"
          @click="emit('key', 'backspace')"
        >
          <LeBackspace role="presentation" width="24" height="24" />
        </button>
      </div>
      <div class="arabic-keyboard__keyboard-row">
        <button
          v-matomo-event:click="{
            category: MatomoEventCategory.ArabicKeyboard,
            action: MatomoEventAction.ArabicKeyboard,
            label: EVENT_LABEL_KEY_PRESS
          }"
          class="arabic-keyboard__button arabic-keyboard__button--space"
          type="button"
          @click="emit('key', ' ')"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span class="mr-2 font-weight-normal">Space</span><span>مسافة</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/z-index.less";

.arabic-keyboard {
  position: fixed;
  z-index: @le-arabic-keyboard__z-index;
  overflow: hidden;
  border-radius: 4px;
  background-color: @colors[@secondary][100];
  box-shadow:
    0px 4px 30px rgba(0, 0, 0, 0.2),
    0px 18px 11px -7px rgba(0, 0, 0, 0.1),
    0px 12px 20px -7px rgba(0, 0, 0, 0.1);

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 55px;
    height: 46px;
    margin-bottom: 0;
    padding: 12px 16px;
    border: 1px solid @colors[@secondary][600];
    border-radius: 4px;
    outline: none;
    background-color: @colors[@neutrals][100];
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.375rem;
    cursor: pointer;

    &:not(:last-child) {
      margin-right: 6px;
    }

    &:hover {
      border-color: @colors[@secondary][600];
      background-color: @colors[@secondary][200];
    }

    &:focus {
      border-color: @colors[@secondary][600];
      background-color: @colors[@neutrals][100];
      box-shadow: 0 0 0 4px rgba(176, 209, 203, 0.4);
    }

    &--backspace {
      width: 80px;
      padding: 0;
    }

    &--space {
      width: 240px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 20px 24px;
    background-color: @colors[@neutrals][100];
    color: #020d03;
    cursor: move;
  }

  &__body {
    padding: 20px 8px;
  }

  &__keyboard-row {
    display: flex;
    justify-content: center;

    &:not(:last-child) {
      margin-bottom: 6px;
    }
  }
}
</style>
